import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { DOCTOR } from "@/data";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Expertise", href: "#expertise" },
  { label: "Cancers", href: "#cancers" },
  { label: "Philosophy", href: "#philosophy" },
  { label: "Research", href: "#research" },
  { label: "Reviews", href: "#reviews" },
];

export const Nav = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-black/5 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="text-left leading-tight"
          data-testid="nav-logo"
        >
          <span className="font-serif-display text-xl sm:text-2xl text-[#1A1C1A]">
            Dr. Pranidha Shree
          </span>
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#B9735A] font-semibold">
            Gynecologic Oncology
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="link-underline text-sm text-[#4A4D4A] hover:text-[#1A1C1A] transition-colors"
              data-testid={`nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:+${DOCTOR.phoneIntl}`}
            className="hidden sm:inline-flex items-center gap-2 text-sm text-[#1A1C1A] hover:text-[#B9735A] transition-colors"
            data-testid="nav-call"
          >
            <Phone size={15} /> {DOCTOR.phone}
          </a>
          <button
            onClick={onBook}
            className="hidden sm:inline-flex items-center rounded-full bg-[#B9735A] text-[#FDFBF7] text-sm font-medium px-5 py-2.5 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#B9735A]/30 transition-[transform,box-shadow] duration-300"
            data-testid="nav-book"
          >
            Book Consultation
          </button>
          <button
            className="lg:hidden text-[#1A1C1A] p-1"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-black/5"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left text-[#4A4D4A] text-base"
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { setOpen(false); onBook(); }}
                className="mt-2 rounded-full bg-[#B9735A] text-[#FDFBF7] text-sm font-medium px-5 py-3"
                data-testid="nav-mobile-book"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
